import React, { useState, useEffect } from 'react';
import { Eye, Heart, Share2, MessageSquare, Activity, Image, Play, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

// Post Detail Modal Component with Lazy Loading Media
export function PostDetailModal({ selectedPost, onClose, channelType = 'public' }) {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  
  // Lazy load media when modal opens
  useEffect(() => {
    if (selectedPost && (selectedPost.has_photo || selectedPost.has_video)) {
      setMediaLoading(true);
      setMediaError(false);
      setMediaUrl(null);
      
      // Fetch media from backend
      const token = localStorage.getItem('token');
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/mtproto/media/${channelType}/${selectedPost.message_id}`;
      
      fetch(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => {
          if (response.ok && response.headers.get('content-type')?.includes('image')) {
            return response.blob();
          }
          throw new Error('Failed to load media');
        })
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setMediaUrl(url);
          setMediaLoading(false);
        })
        .catch(err => {
          console.error('Media load error:', err);
          setMediaError(true);
          setMediaLoading(false);
        });
    }
    
    // Cleanup blob URL on unmount
    return () => {
      if (mediaUrl) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [selectedPost?.message_id, channelType]);
  
  if (!selectedPost) return null;
  
  return (
    <Dialog open={!!selectedPost} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <MessageSquare size={20} className="text-[#E63946]" />
            Post Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Post Meta */}
          <div className="flex items-center gap-3 text-sm text-[#9CA3AF]">
            <Badge className={`${
              selectedPost.has_photo ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : 
              selectedPost.has_video ? 'bg-[#EC4899]/20 text-[#EC4899]' : 
              'bg-[#06B6D4]/20 text-[#06B6D4]'
            }`}>
              {selectedPost.has_photo ? 'Photo' : selectedPost.has_video ? 'Video' : 'Text'}
            </Badge>
            <span>{selectedPost.date ? format(new Date(selectedPost.date), 'dd MMM yyyy, HH:mm') : 'Unknown date'}</span>
          </div>
          
          {/* Media Preview (Lazy Loaded) */}
          {(selectedPost.has_photo || selectedPost.has_video) && (
            <div className="relative bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
              {mediaLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
                  <span className="ml-2 text-sm text-[#9CA3AF]">Loading media...</span>
                </div>
              ) : mediaError ? (
                <div className="flex items-center justify-center h-48 text-[#6B7280]">
                  <Image size={32} className="mr-2 opacity-50" />
                  <span className="text-sm">Media unavailable</span>
                </div>
              ) : mediaUrl ? (
                <div className="relative">
                  <img 
                    src={mediaUrl} 
                    alt="Post media" 
                    className="w-full max-h-[300px] object-contain"
                  />
                  {selectedPost.has_video && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                        <Play size={24} className="text-[#E63946] ml-1" />
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
          
          {/* Post Content */}
          <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 border border-[hsl(var(--border))]">
            <p className="text-sm text-white whitespace-pre-wrap">
              {selectedPost.text || selectedPost.caption || 'No text content'}
            </p>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-[#3B82F6]/10 rounded-lg p-3 text-center border border-[#3B82F6]/20">
              <Eye size={18} className="mx-auto mb-1 text-[#3B82F6]" />
              <p className="text-lg font-bold text-[#3B82F6]">{(selectedPost.views || 0).toLocaleString()}</p>
              <p className="text-[10px] text-[#6B7280]">Views</p>
            </div>
            <div className="bg-[#E63946]/10 rounded-lg p-3 text-center border border-[#E63946]/20">
              <Heart size={18} className="mx-auto mb-1 text-[#E63946]" />
              <p className="text-lg font-bold text-[#E63946]">{selectedPost.reactionCount || 0}</p>
              <p className="text-[10px] text-[#6B7280]">Reactions</p>
            </div>
            <div className="bg-[#10B981]/10 rounded-lg p-3 text-center border border-[#10B981]/20">
              <Share2 size={18} className="mx-auto mb-1 text-[#10B981]" />
              <p className="text-lg font-bold text-[#10B981]">{(selectedPost.forwards || 0).toLocaleString()}</p>
              <p className="text-[10px] text-[#6B7280]">Forwards</p>
            </div>
            <div className="bg-[#8B5CF6]/10 rounded-lg p-3 text-center border border-[#8B5CF6]/20">
              <Activity size={18} className="mx-auto mb-1 text-[#8B5CF6]" />
              <p className="text-lg font-bold text-[#8B5CF6]">{selectedPost.postErr?.toFixed(2) || '0.00'}%</p>
              <p className="text-[10px] text-[#6B7280]">ERR</p>
            </div>
          </div>
          
          {/* Reaction Breakdown */}
          {selectedPost.reactions && Object.keys(selectedPost.reactions).length > 0 && (
            <div className="bg-[hsl(var(--surface-elevated))] rounded-lg p-4 border border-[hsl(var(--border))]">
              <p className="text-sm text-white font-medium mb-3">Reaction Breakdown</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(selectedPost.reactions).map(([emoji, count]) => {
                  if (typeof count !== 'number') return null;
                  return (
                    <div key={emoji} className="flex items-center gap-1 bg-[hsl(var(--surface))] px-3 py-1.5 rounded-full border border-[hsl(var(--border))]">
                      <span className="text-lg">{emoji}</span>
                      <span className="text-sm text-white font-medium">{count.toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Reach Info */}
          <div className="flex items-center justify-between text-sm p-3 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))]">
            <span className="text-[#9CA3AF]">Post Reach</span>
            <span className="text-white font-medium">{selectedPost.postReach?.toFixed(1) || '0.0'}% of subscribers</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PostDetailModal;
